import { CheckIcon, UsersIcon } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useCheckInGuest } from '@/api/visitors/check-in-guest'
import { useGetGuestDetails } from '@/api/visitors/get-guest-details'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { BorderLoader } from '@/components/ui/loaders/border-loader'
import { Modal } from '@/components/ui/modal'
import { EMPTY_VALUE } from '@/constants/ui'
import { toastFactory } from '@/factories'
import { visitorStatusBadgeColorMap } from '@/models'
import {
  formatDateTime,
  formatPermissions,
  formatPermissionTimeRange,
} from '@/pages/visitors-dashboard/visitors-dashboard.utils'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { getApiErrorMessage } from '@/utils'

import * as S from './guest-details-modal.styled'
import { GuestDetailsModalProps } from './guest-details-modal.types'

export const GuestDetailsModal = ({ clientId, guestId, onClose }: GuestDetailsModalProps) => {
  const { t } = useTranslation(['visitors', 'common'])
  const building = useSelector(selectActiveBuilding)
  const timezone = building?.timezone ?? 'America/Chicago'
  const showKeylessAccess = Boolean(
    (building?.saltoSpaceAvailable && building?.saltoSpaceVisitorAccessAvailable) ||
      (building?.remoteLockAvailable && building?.remoteLockVisitorAccessAvailable) ||
      (building?.iglooWorkAvailable && building?.iglooWorkVisitorAccessAvailable),
  )

  const { data, isLoading } = useGetGuestDetails(guestId)
  const { mutateAsync: checkInGuest, isPending: isCheckingIn } = useCheckInGuest()

  const visitor = data?.visitor
  const checkIns = data?.checkIns ?? []
  const timeRange = visitor ? formatPermissionTimeRange(visitor, timezone) : null

  const handleCheckIn = async () => {
    try {
      await checkInGuest({ clientId: clientId!, guestId })
      toastFactory.success(
        t('visitors:dashboard.snackbar.checkedIn', {
          name: visitor?.name ?? t('visitors:modalTypes.guest'),
        }),
      )
      onClose()
    } catch (error) {
      toastFactory.error(
        getApiErrorMessage(error) ??
          t('visitors:dashboard.snackbar.errorCheckingIn', {
            type: t('visitors:snackbar.typeGuest'),
          }),
      )
    }
  }

  const footer = (
    <>
      <Button use="secondary" onClick={onClose}>
        {t('common:actions.close')}
      </Button>
      {clientId !== undefined && visitor?.status === 'active' && (
        <Button disabled={isCheckingIn} onClick={handleCheckIn}>
          {t('visitors:columns.checkIn')} <Icon icon={CheckIcon} size="1.4rem" />
        </Button>
      )}
    </>
  )

  return (
    <Modal
      title={t('visitors:detailsModal.guestVendorTitle', { type: t('visitors:modalTypes.guest') })}
      headerIcon={UsersIcon}
      height="74.8rem"
      footer={footer}
      onClose={onClose}
    >
      {isLoading || !visitor ? (
        <BorderLoader align="absolute-center" />
      ) : (
        <S.Content>
          {visitor.notes && (
            <S.Section>
              <S.SectionTitle>{t('visitors:detailsModal.note')}</S.SectionTitle>
              <S.ItemsContainer>
                <S.Item>{visitor.notes}</S.Item>
              </S.ItemsContainer>
            </S.Section>
          )}

          <S.Section>
            <S.SectionTitle>{t('visitors:detailsModal.guestInformation')}</S.SectionTitle>
            <S.ItemsContainer>
              <S.Item>
                <span>{t('visitors:detailsModal.guestName')}</span>
                <S.ItemValue>{visitor.name || EMPTY_VALUE}</S.ItemValue>
              </S.Item>
              <S.Item>
                <span>{t('visitors:detailsModal.guestStatus')}</span>
                <Badge
                  color={visitorStatusBadgeColorMap[visitor.status]}
                  size="small"
                  variant="light"
                >
                  {visitor.status.charAt(0).toUpperCase() + visitor.status.slice(1)}
                </Badge>
              </S.Item>
              <S.Item>
                <span>{t('visitors:detailsModal.email')}</span>
                <S.ItemValue>{visitor.email || EMPTY_VALUE}</S.ItemValue>
              </S.Item>
              <S.Item>
                <span>{t('visitors:detailsModal.phone')}</span>
                <S.ItemValue>{visitor.phone || EMPTY_VALUE}</S.ItemValue>
              </S.Item>
              <S.Item>
                <span>{t('visitors:detailsModal.company')}</span>
                <S.ItemValue>{visitor.company || EMPTY_VALUE}</S.ItemValue>
              </S.Item>
              <S.Item>
                <span>{t('visitors:detailsModal.guestType')}</span>
                <S.ItemValue>{visitor.type || EMPTY_VALUE}</S.ItemValue>
              </S.Item>
              {visitor.status === 'banned' && visitor.bannedReason && (
                <S.Item>
                  <span>{t('visitors:detailsModal.banReason')}</span>
                  <S.ItemValue>{visitor.bannedReason}</S.ItemValue>
                </S.Item>
              )}
            </S.ItemsContainer>
          </S.Section>

          <S.Section>
            <S.SectionTitle>{t('visitors:detailsModal.permissionAndVisitDetails')}</S.SectionTitle>
            <S.ItemsContainer>
              <S.Item>
                <span>{t('visitors:detailsModal.permissionType')}</span>
                <S.ItemValue>
                  {formatPermissions(visitor, timezone)}
                  {timeRange && `, ${timeRange}`}
                </S.ItemValue>
              </S.Item>
              <S.Item>
                <span>{t('visitors:detailsModal.startTime')}</span>
                <S.ItemValue>
                  {visitor.startTime
                    ? formatDateTime(visitor.startTime, timezone).time
                    : EMPTY_VALUE}
                </S.ItemValue>
              </S.Item>
              <S.Item>
                <span>{t('visitors:detailsModal.endTime')}</span>
                <S.ItemValue>
                  {visitor.endTime ? formatDateTime(visitor.endTime, timezone).time : EMPTY_VALUE}
                </S.ItemValue>
              </S.Item>
              <S.Item>
                <span>{t('visitors:detailsModal.createdBy')}</span>
                <S.ItemValue>{visitor.createdBy || EMPTY_VALUE}</S.ItemValue>
              </S.Item>
              {building?.customAccessAvailable && (
                <S.Item>
                  <span>{building.customAccessName}</span>
                  <S.ItemValue>
                    {visitor.customAccess ? t('common:booleans.yes') : t('common:booleans.no')}
                  </S.ItemValue>
                </S.Item>
              )}
              {/* Note: As it was described in EL-14633 - keylessAccess is temporarly treated as
              physical key access, // and keyAccess is treated as keyless integration */}
              <S.Item>
                <span>{t('visitors:detailsModal.physicalKeyRelease')}</span>
                <S.ItemValue>
                  {visitor.keylessAccess ? t('common:booleans.yes') : t('common:booleans.no')}
                </S.ItemValue>
              </S.Item>
              {/* Note: As it was described in EL-14633 - keylessAccess is temporarly treated as
              physical key access, // and keyAccess is treated as keyless integration */}
              <S.Item>
                <span>{t('visitors:detailsModal.keylessAccess')}</span>
                <S.ItemValue>
                  {visitor.keyAccess ? t('common:booleans.yes') : t('common:booleans.no')}
                </S.ItemValue>
              </S.Item>
              {building?.brivoAvailable && (
                <S.Item>
                  <span>{t('visitors:detailsModal.pinCodeAccess')}</span>
                  <S.ItemValue>
                    {visitor.pinCode ? t('common:booleans.yes') : t('common:booleans.no')}
                  </S.ItemValue>
                </S.Item>
              )}
              <S.Item>
                <span>{t('visitors:detailsModal.guestId')}</span>
                <S.ItemValue>{visitor.guestId || visitor.vendorId || EMPTY_VALUE}</S.ItemValue>
              </S.Item>
            </S.ItemsContainer>
          </S.Section>

          <S.Section>
            <S.SectionTitle>{t('visitors:detailsModal.residentDetails')}</S.SectionTitle>
            <S.ItemsContainer>
              <S.Item>
                <span>{t('visitors:detailsModal.residentName')}</span>
                <S.ItemValue>{visitor.residentName || EMPTY_VALUE}</S.ItemValue>
              </S.Item>
              <S.Item>
                <span>{t('visitors:detailsModal.residentUnitNumber')}</span>
                <S.ItemValue>{visitor.unitNumber || EMPTY_VALUE}</S.ItemValue>
              </S.Item>
              <S.Item>
                <span>{t('visitors:detailsModal.residentPhone')}</span>
                <S.ItemValue>{visitor.residentPhone || EMPTY_VALUE}</S.ItemValue>
              </S.Item>
            </S.ItemsContainer>
          </S.Section>

          {checkIns.length > 0 && (
            <S.Section>
              <S.SectionTitle>{t('visitors:detailsModal.checkInLog')}</S.SectionTitle>
              <S.ItemsContainer>
                {checkIns.map((log, index) => {
                  const { date, time } = formatDateTime(log.date, timezone)
                  return (
                    <S.Item key={index}>
                      <span>{date}</span>
                      <S.ItemValue>{time}</S.ItemValue>
                    </S.Item>
                  )
                })}
              </S.ItemsContainer>
            </S.Section>
          )}
        </S.Content>
      )}
    </Modal>
  )
}
